import { EntityFormModal } from '@/components/EntityFormModal'
import { useSeededModalField } from '@/hooks/useSeededModalField'
import { evaluateIdentifierField, IDENTIFIER_RULE_MESSAGE } from '@/lib/identifierValidation'
import type { ExtractorEntry } from '@/types/bundle'
import { buildEmptyExtractor, cloneForDuplicateExtractor } from '../constants'

export interface NewOrDuplicateExtractorModalProps {
  open: boolean
  onClose: () => void
  existingIds: string[]
  /** Present for 'duplicate', absent for '+ New' - drives title/prefill logic,
   * same pattern as product-types' own NewOrDuplicateProductTypeModal. */
  source?: { id: string; extractor: ExtractorEntry }
  onConfirm: (newId: string, initialEntry: ExtractorEntry) => void
}

// Both "+ New Extractor" and per-row "Duplicate" open this same form (empty
// vs. pre-filled) - mirrors NewOrDuplicateProductTypeModal's own pattern
// exactly. An extractor id is mandatory either way since JSON object keys
// can't literally duplicate.
export function NewOrDuplicateExtractorModal({
  open,
  onClose,
  existingIds,
  source,
  onConfirm,
}: NewOrDuplicateExtractorModalProps) {
  const openKey = open ? (source?.id ?? '__new__') : null
  const { value: id, setValue: setId, reset } = useSeededModalField(openKey, () =>
    source ? `${source.id}_copy` : '',
  )

  const { trimmedId, isDuplicateId, isInvalidShape, isValid } = evaluateIdentifierField(id, existingIds)

  function handleClose() {
    reset()
    onClose()
  }

  function handleConfirm() {
    if (!isValid) return
    const initialEntry = source ? cloneForDuplicateExtractor(source.extractor) : buildEmptyExtractor()
    onConfirm(trimmedId, initialEntry)
    handleClose()
  }

  return (
    <EntityFormModal
      open={open}
      entityLabel="Extractor"
      isDuplicate={Boolean(source)}
      isValid={isValid}
      onClose={handleClose}
      onConfirm={handleConfirm}
    >
      <div className="flex flex-col gap-1">
        <label className="text-meta font-medium text-text-secondary" htmlFor="new-extractor-id">
          Extractor ID
        </label>
        <input
          id="new-extractor-id"
          type="text"
          autoFocus
          value={id}
          onChange={(event) => setId(event.target.value)}
          placeholder="e.g. bedrock_vision"
          className="rounded-control border border-border bg-bg px-3 py-2 text-body text-text-primary outline-none placeholder:text-text-disabled"
        />
        {isDuplicateId && <span className="text-meta text-danger">An extractor with this ID already exists</span>}
        {isInvalidShape && <span className="text-meta text-danger">{IDENTIFIER_RULE_MESSAGE}</span>}
      </div>
    </EntityFormModal>
  )
}
